import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '@/contexts/LanguageContext';
import type { DepartmentHours, DayHours } from '@/utils/messageParser/types';

interface OpeningHoursMessageProps {
  openingHours: DepartmentHours[];
  singleColumn?: boolean;
  showTitle?: boolean;
}

const OpeningHoursMessage: React.FC<OpeningHoursMessageProps> = ({ 
  openingHours, 
  singleColumn = false, 
  showTitle = false 
}) => {
  const { language, t } = useLanguage();
  
  // Language-aware day names
  const DAYS_EN_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const DAYS_FR_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  const DAYS_EN_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const DAYS_FR_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  
  // Use full names for single column, short names for two column layout
  const DAYS = singleColumn 
    ? (language === 'fr' ? DAYS_FR_FULL : DAYS_EN_FULL)
    : (language === 'fr' ? DAYS_FR_SHORT : DAYS_EN_SHORT);
  
  // Language-aware "Closed" text - use translation system for proper encoding
  const CLOSED_TEXT = t('hours.closed');

  // Add safety check for empty/undefined data
  if (!openingHours || openingHours.length === 0) {
    return null;
  }
  
  const formatTime = (time: string, amPm: string): string => {
    // Handle empty, undefined, or invalid time values
    if (!time || time === '00:00' || time === '0:00' || time.trim() === '') {
      return '';
    }
    return `${time}${amPm}`;
  };

  const formatHours = (opening: string, openingAmPm: string, closing: string, closingAmPm: string): string => {
    // Check if times are missing or invalid
    const openingTime = formatTime(opening, openingAmPm);
    const closingTime = formatTime(closing, closingAmPm);
    
    if (!openingTime || !closingTime) {
      return CLOSED_TEXT;
    }
    
    return `${openingTime} - ${closingTime}`;
  };

  const getTitle = (departmentName: string) => {
    return language === 'fr' 
      ? `Heures d'ouverture - ${departmentName}`
      : `${departmentName} Opening Hours`;
  };

  return (
    <div className="my-1 max-w-sm">
      {openingHours.map((department, deptIndex) => (
        <div key={deptIndex} className="bg-gray-50 rounded d2cbot-bot_border d2cbot-bot_border-gray-200 p-1.5 shadow-sm mb-1.5 last:mb-0">
          {/* Department header with optional title */}
          <div className="font-semibold text-xs text-foreground mb-1.5 flex items-center">
            <FontAwesomeIcon icon={faClock} className="mr-1.5 text-gray-500" />
            {showTitle ? getTitle(department.Department) : department.Department}
          </div>
          
          {/* Conditional layout based on singleColumn prop */}
          {singleColumn ? (
            /* Single column layout */
            <div className="space-y-0.5 text-xs">
              {department.Days.map((dayHours, dayIndex) => (
                <div key={dayIndex} className="flex justify-between items-center">
                  <span className="font-medium text-xs">{DAYS[dayHours.day - 1]}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatHours(dayHours.opening, dayHours.opening_am_pm, dayHours.closing, dayHours.closing_am_pm)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            /* Two column layout with separator */
            <div className="flex text-xs">
              {/* Left column: Monday-Friday */}
              <div className="flex-1 pr-4 space-y-0.5">
                {department.Days.slice(0, 5).map((dayHours, dayIndex) => (
                  <div key={dayIndex} className="flex justify-between items-center">
                    <span className="font-medium text-xs">{DAYS[dayHours.day - 1]}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatHours(dayHours.opening, dayHours.opening_am_pm, dayHours.closing, dayHours.closing_am_pm)}
                    </span>
                  </div>
                ))}
              </div>
              
              {/* Vertical separator */}
              <div className="w-px bg-gray-300 mx-4"></div>
              
              {/* Right column: Saturday-Sunday */}
              <div className="flex-1 pl-4 space-y-0.5">
                {department.Days.slice(5, 7).map((dayHours, dayIndex) => (
                  <div key={dayIndex + 5} className="flex justify-between items-center">
                    <span className="font-medium text-xs">{DAYS[dayHours.day - 1]}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatHours(dayHours.opening, dayHours.opening_am_pm, dayHours.closing, dayHours.closing_am_pm)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default OpeningHoursMessage;